import React, {useState, useEffect}					from 'react';
import {useNavigate}								from 'react-router-dom';

import MdmContent									from './MdmMain';
import ExternalApiCard								from '../../ressources/components/ExternalApiCard';
import Modal										from '../../ressources/components/Modal';
import Icon											from '../../ressources/components/Icon';
import MultiInputContainer							from '../../ressources/components/Input/MultiInputContainer';
import Input										from '../../ressources/components/Input';
import {InputValues}								from '../../ressources/components/Input/interface';
import Textarea										from '../../ressources/components/Textarea';

import {useAppDispatch, useAppSelector}				from '../../app/hooks';
import {LanguageType}								from '../../ressources/language/type';
import {SelectData}									from '../../ressources/components/Select/interface';
import {ApiMdmPostConnect}							from '../../redux/devices/interfaceMdm';
import {DevicesState}								from '../../redux/devices/interface';

import {ft_genNotification, ft_pushNotification} 	from '../../ressources/functions/notification';
import ft_cssVar									from '../../ressources/functions/getCssVar';
import {regex_email}								from '../../ressources/data/regex';

import img_settings									from '../../ressources/images/icon/menu/settings.svg';
import img_refresh									from '../../ressources/images/icon/refresh.svg';
import img_trash									from '../../ressources/images/icon/trash.svg';
import img_power									from '../../ressources/images/icon/power.svg';
import img_send										from '../../ressources/images/icon/send.svg';
import img_link										from '../../ressources/images/icon/link.svg';

import LANGUAGE										from '../../ressources/language';
import * as API										from '../../ressources/API';

import './Mdm.css';

type MODAL = 'learnMoreJamf' | 'askLicense' | 'askLicenseConfirm' | 'connectJamf' | 'connectVmWare' | null;

function DevicesMdm() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const company = useAppSelector(state => state.company.data);
	const lang = useAppSelector(state => state.settings.lang);
	const userReload = useAppSelector(state => state.user.reload);
	const notificationId = useAppSelector(state => state.notifications.lastId);

	const [connected, set_connected] = useState<(keyof DevicesState['mdm'] | 'jamfProtect')[]>([]);

	const [modal, set_modal] = useState<MODAL>(null);

	// --- Connect form
	const [url, set_url] = useState<InputValues>('');
	const [key, set_key] = useState<InputValues>('');
	const [login, set_login] = useState<InputValues>('');
	const [password, set_password] = useState<InputValues>('');

	// --- Ask License form
	const [phone, set_phone] = useState<InputValues>('');
	const [email, set_email] = useState<InputValues>('');
	const [message, set_message] = useState<InputValues>('');

	const [load, set_load] = useState(false);

	const navigate = useNavigate();
	const TEXTE = LANGUAGE(lang);

	useEffect(() => {
		if (!user)
			return;

		set_phone(user.phone);
		set_email(user.email);
	}, [user]);

	useEffect(getMdm, [userReload]);

	function getMdm() {
		API.getMdm(user)
		.then(response => set_connected(response.data || []));
	}

	function connectMdm(name: keyof DevicesState['mdm']) {
		set_load(true);

		const apiData: ApiMdmPostConnect = {
			url,
			password,
			user: login,
			key:  name === 'vmWare' ? key : undefined
		};

		API.postMdm(name, apiData, user)
		.then((response) => {
			set_load(false);

			if (!response.status) {
				const notificationData = ft_genNotification(TEXTE, 'mdmConnectError', img_link, undefined, 'error');
				return ft_pushNotification(dispatch, notificationId, notificationData);
			}

			const notificationData = ft_genNotification(TEXTE, 'mdmConnectSuccess', img_link, TEXTE.component.externalApi.jamf);
			ft_pushNotification(dispatch, notificationId, notificationData);

			set_modal(null);
			getMdm();
		});
	}

	function sendLicenseForm() {
		set_load(true);

		API.postMdmLicenseForm({email, phone, message}, user)
		.then((response) => {
			set_load(false);

			if (response.status)
				return set_modal('askLicenseConfirm');

			const notificationData = ft_genNotification(TEXTE, 'error', img_send, response.payload?.error, 'error');
			return ft_pushNotification(dispatch, notificationId, notificationData);
		});
	}

	function renderImportButton(name: keyof LanguageType['component']['externalApi'], disabled?: boolean, learnMore?: boolean) {
		const isConnected = !disabled && connected.find(find => find === name);
		const ACTIONS_LIST: SelectData[] = [];

		if (isConnected) {
			ACTIONS_LIST.push({
				value:  'import',
				render: <span className={'text-xs weight-600 gray-700'}>{TEXTE.users.hris.import}</span>
			});
		}

		return (
			<ExternalApiCard
				name={name}
				disabled={disabled}
				actionList={ACTIONS_LIST}
				isConnected={!!isConnected}
				noConnect={name === 'vmWare'}
				text={TEXTE.devices.mdm.syncText}
				title={TEXTE.devices.mdm.syncDevices}
				learnMore={learnMore ? () => set_modal('learnMoreJamf') : undefined}
				onClickAction={(action) => {
					if (isConnected && action === 'import')
						return navigate(`/devices/mdm/${name}/import`);
					if (action === 'connect' && name === 'jamf')
						return set_modal('connectJamf');
				}}
			/>
		);
	}

	function renderModalLearnItem(icon: string, title: keyof LanguageType['devices']['mdm'], text?: keyof LanguageType['devices']['mdm']) {
		return (
			<div className={'deviceMdmLearnMoreItem'}>
				<div className={'deviceMdmLearnMoreItemIcon'}>
					<Icon
						icon={icon}
						iconSize={20}
						iconColor={[ft_cssVar('primary-600'), ft_cssVar('primary-600')]}
					/>
				</div>
				<div className={'deviceMdmLearnMoreItemText'}>
					<span className={'text-lg weight-600 gray-600'}>{TEXTE.devices.mdm[title]}</span>
					{text && <span className={'gray-900'}>{TEXTE.devices.mdm[text]}</span>}
				</div>
			</div>
		);
	}

	function renderModal() {
		if (modal === 'learnMoreJamf') {
			return (
				<Modal
					size={632}
					onClose={() => set_modal(null)}
					confirmButton={() => set_modal('askLicense')}
					confirmButtonText={TEXTE.devices.mdm.askLicense}
					header={<div className={'externalApiCardLogo jamf'} />}
				>
					<span className={'text-xl weight-700 gray-900'}>{TEXTE.devices.mdm.learnMoreJamfTitle}</span>
					<span className={'weight-500 primary-600'}>{TEXTE.devices.mdm.fromPerMonth}</span>

					{renderModalLearnItem(img_settings, 'setPreConfiguration', 'setPreConfigurationDetail')}
					{renderModalLearnItem(img_refresh, 'restartRemotely')}
					{renderModalLearnItem(img_trash, 'eraseDeviceRemotely')}
					{renderModalLearnItem(img_power, 'shutdownRemotely')}
				</Modal>
			);
		}

		if (modal === 'askLicense') {
			return (
				<Modal
					gap={8}
					size={820}
					confirmLoad={load}
					confirmButton={sendLicenseForm}
					onClose={() => set_modal(null)}
					confirmButtonDisabled={!message}
					confirmButtonText={TEXTE.devices.mdm.send}
					header={
						<div className={'deviceMdmLicenseFormHeader'}>
							<div className={'externalApiCardLogo jamf'} />
							<div className={'flex flex-column'}>
								<span>{TEXTE.devices.mdm.askJamfLicense}</span>
								<span className={'text-sm weight-400 gray-600'}>{TEXTE.devices.mdm.askJamfLicenseText}</span>
							</div>
						</div>
					}
				>
					<MultiInputContainer>
						<Input
							readOnly
							value={user?.firstname}
							title={TEXTE.global.firstname}
						/>
						<Input
							readOnly
							value={user?.lastname}
							title={TEXTE.global.lastname}
						/>
					</MultiInputContainer>
					<MultiInputContainer>
						<Input
							value={phone}
							onChange={set_phone}
							title={TEXTE.global.phone}
						/>
						<Input
							value={email}
							onChange={set_email}
							title={TEXTE.global.email}
						/>
					</MultiInputContainer>
					<Input
						readOnly
						value={company?.name}
						title={TEXTE.settings.myCompany}
					/>

					<Textarea
						resize
						value={message}
						onChange={set_message}
						style={{marginBottom: 4}}
						title={TEXTE.devices.mdm.message}
					/>
				</Modal>
			);
		}

		if (modal === 'askLicenseConfirm') {
			return (
				<Modal
					onClose={() => set_modal(null)}
					header={
						<div className={'flex flex-center'}>
							<Icon
								size={32}
								iconBorder
								borderSize={0}
								icon={img_send}
								type={'success'}
								iconColor={ft_cssVar('success-600')}
							/>
						</div>
					}
				>
					<div style={{whiteSpace: 'pre-wrap'}} className={'flex flex-column text-center'}>
						<span className={'text-xl weight-700 gray-900'}>{TEXTE.devices.mdm.sendConfirm}</span>
						<span className={'text-md'}>{TEXTE.devices.mdm.sendConfirmText}</span>
					</div>
				</Modal>
			);
		}

		if (modal === 'connectJamf') {
			return (
				<Modal
					size={503}
					confirmLoad={load}
					confirmButton={() => connectMdm('jamf')}
					onClose={() => set_modal(null)}
					footerFlexDirection={'column-reverse'}
					cancelButton={() => set_modal('askLicense')}
					cancelButtonText={TEXTE.devices.mdm.noAccount}
					confirmButtonText={TEXTE.devices.mdm.connectToJamf}
					header={<div className={'externalApiCardLogo jamf flex flex-center'} />}
					confirmButtonDisabled={!url || !regex_email.test(email as string) || !password}
				>
					<span className={'display-xs weight-700 gray-900 text-center'}>{TEXTE.devices.mdm.loggingToJamf}</span>
					<span className={'text-md gray-500 text-center'} style={{marginTop: 16}}>
						{TEXTE.devices.mdm.connectionBetweenJamfAndValala}
					</span>

					<div className={'globalFormContainer'} style={{marginTop: 32}}>
						<Input
							value={url}
							onChange={set_url}
							title={TEXTE.devices.mdm.jamfUrl}
						/>
						<Input
							value={login}
							onChange={set_login}
							title={TEXTE.devices.mdm.jamfLogin}
							placeholder={TEXTE.placeholder.email}
						/>
						<Input
							type={'password'}
							value={password}
							onChange={set_password}
							placeholder={'**********'}
							title={TEXTE.devices.mdm.jamfPassword}
						/>
					</div>
				</Modal>
			);
		}

		if (modal === 'connectVmWare') {
			return (
				<Modal
					size={503}
					confirmLoad={load}
					confirmButton={() => connectMdm('vmWare')}
					onClose={() => set_modal(null)}
					footerFlexDirection={'column-reverse'}
					// cancelButton={() => set_modal('askLicense')}
					// cancelButtonText={TEXTE.devices.mdm.noAccount}
					confirmButtonText={TEXTE.devices.mdm.connectToVmWare}
					header={<div className={'externalApiCardLogo vmWare flex flex-center'} />}
					confirmButtonDisabled={!url || !key || !regex_email.test(email as string) || !password}
				>
					<span className={'display-xs weight-700 gray-900 text-center'}>{TEXTE.devices.mdm.loggingToVmWare}</span>
					<span className={'text-md gray-500 text-center'} style={{marginTop: 16}}>
						{TEXTE.devices.mdm.connectionVmWareAndValala}
					</span>

					<div className={'globalFormContainer'} style={{marginTop: 32}}>
						<Input
							value={url}
							onChange={set_url}
							title={TEXTE.devices.mdm.vmWareUrl}
						/>
						<Input
							value={key}
							onChange={set_key}
							title={TEXTE.devices.mdm.vmWareKey}
						/>
						<Input
							value={login}
							onChange={set_login}
							title={TEXTE.devices.mdm.vmWareLogin}
							placeholder={TEXTE.placeholder.email}
						/>
						<Input
							type={'password'}
							value={password}
							onChange={set_password}
							placeholder={'**********'}
							title={TEXTE.devices.mdm.vmWarePassword}
						/>
					</div>
				</Modal>
			);
		}
	}

	return (
		<div className={'globalDataContainer'}>
			<div className={'flex-row'} style={{gap: 16}}>
				{renderImportButton('jamf', false, true)}
				{renderImportButton('vmWare', false)}
			</div>

			{connected.length > 0 &&
				<MdmContent mdmConnected={connected.filter(e => e !== 'jamfProtect') as (keyof DevicesState['mdm'])[]} />
			}


			{modal && renderModal()}
		</div>
	);
}

export default DevicesMdm;
